import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { BlockLifemap, LifemapQuestion } from '@shared/models/step.model';
import { Answers, AnswerValue } from '@shared/services/lifemap/lifemap.service';
import { PlIcon } from '@src/app/icon-library/icons';
import { IconFrame } from '@src/app/icon-library/lib/pl-icon.component';
import { Swiper, SwiperOptions } from 'swiper/types';
import { CustomErrorsLifemapQuestions } from '@app/components/blocks/block-lifemap/lifemapQuestions/lifemapQuestionValidator';
import { NgOnInitWriteBugAbstractComponent } from '@shared/abstract/ngOnInitWriteBug.abstract.component';
import { A11y } from 'swiper/modules';

@Component({
  selector: 'pl-lifemap-questions',
  templateUrl: './lifemapQuestions.component.html',
  styleUrls: ['./lifemapQuestions.component.scss'],
})
export class LifemapQuestionsComponent
  extends NgOnInitWriteBugAbstractComponent<Answers | undefined>
  implements OnInit, ControlValueAccessor
{
  @Input({ required: true }) public blockLifemap!: BlockLifemap;
  @Input() public isFormSubmitted: boolean = false;

  public swiperInstance: Swiper | null = null;
  public config: SwiperOptions | null = null;

  public answers: Answers = [null, null, null, null, null, null, null, null, null, null];

  public disablePrevSlideButton: WritableSignal<boolean> = signal(true);
  public disableNextSlideButton: WritableSignal<boolean> = signal(false);
  public questions: WritableSignal<LifemapQuestion[]> = signal([]);

  public readonly CustomErrorsLifemapQuestions: typeof CustomErrorsLifemapQuestions = CustomErrorsLifemapQuestions;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  public plOnInit(): void {
    this.questions.set(this.blockLifemap.data.config.questions);

    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        const validators: ValidatorFn[] = control.validator ? [control.validator] : [];
        control.setValidators([this.validate.bind(this, validators)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.config = {
      modules: [A11y],
      a11y: {
        enabled: true,
      },
      keyboard: true,
      navigation: false,
      pagination: false,
      slidesPerView: 1,
      centeredSlides: true,
      loop: false,
      on: {
        init: (swiper: Swiper): void => {
          this.swiperInstance = swiper;
        },
        slideChange: (swiper: Swiper): void => {
          console.log('TONIO LifemapHelpComponent slideChange test=', swiper);
          this.onSlideChange(swiper);
        },
      },
    };
  }

  public get swiper(): Swiper {
    const swiperInstance: Swiper | null = this.swiperInstance;
    if (swiperInstance) {
      return swiperInstance;
    }

    throw new Error(`Swiper is not initialised yet, this should not happen`);
  }

  public onSlideChange(swiper: Swiper): void {
    this.disablePrevSlideButton.set(swiper.activeIndex === 0);
    this.disableNextSlideButton.set(swiper.activeIndex === this.questions().length - 1);
  }

  public onPrevButtonClicked(): void {
    this.swiper.slidePrev();
  }

  public onNextButtonClicked(): void {
    this.swiper.slideNext();
  }

  private validate(validators: ValidatorFn[]): ValidationErrors | null {
    const allErrors: any = {};
    for (const validator of validators) {
      const formControl: UntypedFormControl = new UntypedFormControl(this.controlDir.value);
      const hasError: ValidationErrors | null = validator(formControl);
      if (hasError) {
        Object.entries(hasError).forEach(([errorName, isOnError]: [string, boolean]) => {
          allErrors[errorName] = isOnError;
        });
      }
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public plWriteValue(valueToWrite: Answers | undefined): void {
    if (valueToWrite) {
      this.answers = valueToWrite;
      this.updateValueAndValidity();
    }
  }

  private emitChanges(answers: Answers): void {
    this.updateValueAndValidity();
    this.onTouchedCallback();
    this.onChangeCallback(answers);
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }

  public onQuestionAnswered(indexQuestionSelected: number): void {
    const indexFirstUnansweredQuestion: number | null = this.getIndexFirstAnsweredQuestion(this.answers);
    if (indexFirstUnansweredQuestion !== null) {
      if (indexQuestionSelected === this.questions().length - 1) {
        this.swiper.slideTo(indexFirstUnansweredQuestion);
      } else {
        this.onNextButtonClicked();
      }
    }

    this.onTouchedCallback();
    this.emitChanges(this.answers);
  }

  private getIndexFirstAnsweredQuestion(answers: Answers): number | null {
    for (let index: number = 0; index < answers.length; index++) {
      const answer: AnswerValue = answers[index];
      if (answer === null) {
        return index;
      }
    }

    return null;
  }
}
